/**
 * Keeps an expanded capacity strip from acting as a drop target (AC 19).
 *
 * The strip is rendered into a Bryntum `RowExpander` body, which belongs to the resource row ABOVE
 * it. Left alone, Bryntum resolves a release over the strip to that resource and commits the
 * reassignment — the exact outcome requirement 16 forbids: "it must not be attributed to the
 * resource above it, the resource below it, or the last resource the pointer crossed."
 *
 * TWO HOOKS, NOT ONE, and this is the part that is easy to get wrong. `eventDrag.validatorFn` drives
 * only the drag's APPEARANCE — it can report invalid on every tick while the drop still commits, so
 * a guard wired there alone looks correct and does nothing. The authoritative veto is
 * `context.valid = false` plus `finalize(false)` on `beforeEventDropFinalize`. There is also no
 * `beforeEventDrop` event at all, so a listener on that plausible-looking name fires never and fails
 * silently.
 *
 * The two hooks read the pointer from different places because Bryntum gives them different things:
 * `beforeEventDropFinalize` carries a real `domEvent`, so the veto resolves the element directly and
 * needs no tracking. `validatorFn`'s context carries no DOM reference at all, so the appearance half
 * has to consult a pointer position tracked during the drag.
 */

/** Marks the capacity strip host the band renders into — see `renderCapacityHost`. */
const CAPACITY_HOST_SELECTOR = '[data-capacity-host]';

/**
 * Bryntum's own row-expander body, matched as well as the host.
 *
 * The host does not fill the body — there is chrome around it — so a release landing in that gutter
 * is still a release over the strip as far as a dispatcher is concerned, and must be refused for the
 * same reason. Matching only the host would leave a few pixels along each edge that silently commit
 * the reassignment, which is worse than not guarding at all: it would be intermittent.
 */
const EXPANDER_BODY_SELECTOR = '.b-row-expander-body';

/**
 * The reason surfaced during the drag. AC 19 requires the treatment to be NAMED rather than a bare
 * invalid cursor, so a dispatcher who lands there learns where the task can go instead.
 */
export const CAPACITY_DROP_REASON =
  'Capacity strips are read-only — drop on a resource row to reassign.';

/** The subset of `Element` this needs, kept narrow so tests need no real DOM tree. */
interface ClosestCapable {
  closest(selector: string): unknown;
}

function asClosestCapable(target: unknown): ClosestCapable | null {
  if (!target || typeof target !== 'object') return null;
  const candidate = target as { closest?: unknown };
  return typeof candidate.closest === 'function'
    ? (target as ClosestCapable)
    : null;
}

/**
 * Whether an element sits inside an expanded capacity strip.
 *
 * Anything without a `closest` — null, the document, a detached node — is NOT treated as a strip.
 * Failing open matters here: a false positive would refuse a legitimate reassignment onto a resource
 * row, which is a worse failure than the one being prevented.
 */
export function isCapacityStripTarget(target: unknown): boolean {
  const element = asClosestCapable(target);
  if (!element) return false;

  return Boolean(
    element.closest(CAPACITY_HOST_SELECTOR) ??
      element.closest(EXPANDER_BODY_SELECTOR)
  );
}

/**
 * Whether a drop described by this event landed on a capacity strip.
 *
 * Reads `domEvent.target`, which `beforeEventDropFinalize` supplies and which is the position the
 * pointer was actually released at — not a tracked one that may be a tick stale.
 */
export function isCapacityStripDrop(domEvent: unknown): boolean {
  if (!domEvent || typeof domEvent !== 'object') return false;

  return isCapacityStripTarget((domEvent as { target?: unknown }).target);
}
